import { Input } from "@/components/ui/Input";
import { InputShell } from "@/components/ui/InputShell";
import { useEffect, useState } from "react";
import type { SceneObject } from "@mind-sands/shared";
import {
  AxisInput,
  LabeledRow,
  clampValue,
  POS_MAX,
  POS_MIN,
  SCALE_MAX,
  SCALE_MIN,
} from "./shared";

/**
 * Position / scale / rotation editors for the selected object.
 * Owns the transient input state and emits committed changes via `onChange`.
 */
export function TransformControls({
  object,
  onChange,
}: {
  object: SceneObject;
  onChange: (updates: Partial<SceneObject>) => void;
}) {
  const [posX, setPosX] = useState("");
  const [posY, setPosY] = useState("");
  const [posZ, setPosZ] = useState("");
  const [rotX, setRotX] = useState("");
  const [rotY, setRotY] = useState("");
  const [rotZ, setRotZ] = useState("");
  const [scaleVal, setScaleVal] = useState("");

  useEffect(() => {
    setPosX(object.position[0].toFixed(2));
    setPosY(object.position[1].toFixed(2));
    setPosZ(object.position[2].toFixed(2));
    setRotX(object.rotation[0].toFixed(2));
    setRotY(object.rotation[1].toFixed(2));
    setRotZ(object.rotation[2].toFixed(2));
    setScaleVal(object.scale.toFixed(2));
  }, [object]);

  const handlePositionChange = (axis: "x" | "y" | "z", rawValue: string) => {
    const parsed = parseFloat(rawValue);
    if (isNaN(parsed) || rawValue.trim() === "") {
      if (axis === "x") setPosX(rawValue);
      if (axis === "y") setPosY(rawValue);
      if (axis === "z") setPosZ(rawValue);
      return;
    }

    const clamped = clampValue(parsed, POS_MIN, POS_MAX);
    if (axis === "x") setPosX(clamped);
    if (axis === "y") setPosY(clamped);
    if (axis === "z") setPosZ(clamped);

    const newPosition: [number, number, number] = [
      axis === "x" ? parseFloat(clamped) : object.position[0],
      axis === "y" ? parseFloat(clamped) : object.position[1],
      axis === "z" ? parseFloat(clamped) : object.position[2],
    ];
    onChange({ position: newPosition });
  };

  const handleScaleChange = (rawValue: string) => {
    const parsed = parseFloat(rawValue);
    if (isNaN(parsed) || rawValue.trim() === "") {
      setScaleVal(rawValue);
      return;
    }

    const clamped = clampValue(parsed, SCALE_MIN, SCALE_MAX);
    setScaleVal(clamped);
    onChange({ scale: parseFloat(clamped) });
  };

  const handleRotationChange = (axis: "x" | "y" | "z", rawValue: string) => {
    const parsed = parseFloat(rawValue);
    if (Number.isNaN(parsed) || rawValue.trim() === "") {
      if (axis === "x") setRotX(rawValue);
      if (axis === "y") setRotY(rawValue);
      if (axis === "z") setRotZ(rawValue);
      return;
    }

    // Rotation can be any angle; don't clamp to a fixed range, just round for display.
    const rounded = parsed.toFixed(2);
    if (axis === "x") setRotX(rounded);
    if (axis === "y") setRotY(rounded);
    if (axis === "z") setRotZ(rounded);

    const newRotation: [number, number, number] = [
      axis === "x" ? parseFloat(rounded) : object.rotation[0],
      axis === "y" ? parseFloat(rounded) : object.rotation[1],
      axis === "z" ? parseFloat(rounded) : object.rotation[2],
    ];
    onChange({ rotation: newRotation });
  };

  return (
    <>
      {/* Position */}
      <LabeledRow label="Position">
        <div className="grid grid-cols-3 gap-1.5">
          <AxisInput
            axis="X"
            value={posX}
            onChange={(v) => handlePositionChange("x", v)}
          />
          <AxisInput
            axis="Y"
            value={posY}
            onChange={(v) => handlePositionChange("y", v)}
          />
          <AxisInput
            axis="Z"
            value={posZ}
            onChange={(v) => handlePositionChange("z", v)}
          />
        </div>
      </LabeledRow>

      {/* Scale */}
      <LabeledRow label="Scale">
        <InputShell>
          <span className="text-xs text-sands-400 font-medium">S</span>
          <Input
            type="number"
            step="0.1"
            min={SCALE_MIN}
            max={SCALE_MAX}
            value={scaleVal}
            onChange={(e) => handleScaleChange(e.target.value)}
            accent="none"
            surface="ghost"
            borderWeight="none"
            inputSize="xs"
            className="flex-1 min-w-0 text-sm pl-1 pr-0.5 py-0.5"
          />
        </InputShell>
      </LabeledRow>

      {/* Rotation */}
      <LabeledRow label="Rotation">
        <div className="grid grid-cols-3 gap-1.5">
          <AxisInput
            axis="X"
            value={rotX}
            onChange={(v) => handleRotationChange("x", v)}
          />
          <AxisInput
            axis="Y"
            value={rotY}
            onChange={(v) => handleRotationChange("y", v)}
          />
          <AxisInput
            axis="Z"
            value={rotZ}
            onChange={(v) => handleRotationChange("z", v)}
          />
        </div>
      </LabeledRow>
    </>
  );
}
